import { useTranslation } from 'module/hooks/use-translation';
import { logger } from 'module/logger';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { TextArea } from 'module/ui/components/atoms/input/text-area';
import {
  faSend,
  faWandMagicSparkles,
  faSpinner,
} from '@fortawesome/pro-solid-svg-icons';
import { FC, useState } from 'react';
import styled from 'styled-components';

// Styled components for the AI Query input
const AiQueryContainer = styled.div`
  width: 100%;
  border-radius: 8px;
  margin-top: 16px;
`;

const AiQueryLabel = styled.div`
  font-weight: 600;
  font-size: var(--font-size-sm);
  margin-bottom: 6px;
`;

const AiQueryInputWrapper = styled.div`
  display: flex;
  align-items: start;
  background: #fff;
  border-radius: var(--border-radius-default);
  padding: var(--spacing-02) var(--spacing-03);
  gap: var(--spacing-03);
  border: 1px solid var(--scarlet-gray-40);
`;

const AiQueryTextArea = styled(TextArea)`
  border: none;
  box-shadow: none;
  outline: none;
  padding: 0;
  align-items: center;
  justify-content: center;
  display: flex;
  line-height: 18px;
  border-radius: 0;
`;

const IconWrapper = styled.div``;

const SendIcon = styled(FontAwesomeIcon)<{ $disabled: boolean }>`
  color: ${(props) =>
    props.$disabled ? 'var(--icon-disabled)' : 'var(--icon-primary)'};
  cursor: ${(props) => (props.$disabled ? 'not-allowed' : 'pointer')};
`;

export interface AiQueryInputProps {
  onSubmit: (query: string) => Promise<void>;
  isProcessing?: boolean;
  placeholder?: string;
  label?: string;
}

export const AiQueryInput: FC<AiQueryInputProps> = ({
  onSubmit,
  isProcessing = false,
  placeholder,
  label,
}) => {
  const [aiQuery, setAiQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const translatedPlaceholder = useTranslation('ai_query_placeholder');
  const inputPlaceholder = placeholder ?? translatedPlaceholder;
  const translatedLabel = useTranslation('ai_query_label');
  const inputLabel = label ?? translatedLabel;
  const handleSubmit = async () => {
    if (aiQuery === '' || isProcessing) {
      return;
    }

    try {
      setIsLoading(true);
      await onSubmit(aiQuery);
      setAiQuery('');
      setIsLoading(false);
    } catch (error) {
      logger.error('error in generating ai query', { error });
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <AiQueryContainer>
      <AiQueryLabel>{inputLabel}</AiQueryLabel>
      <AiQueryInputWrapper>
        <IconWrapper>
          <FontAwesomeIcon icon={faWandMagicSparkles} size={12} />
        </IconWrapper>
        <AiQueryTextArea
          value={aiQuery}
          onChange={(e) => {
            setAiQuery(e.target.value);
          }}
          autoSize={{ minRows: 1, maxRows: 10 }}
          onKeyDown={handleKeyDown}
          placeholder={inputPlaceholder}
          rows={1}
        />
        <IconWrapper>
          <SendIcon
            icon={isLoading ? faSpinner : faSend}
            size={12}
            $disabled={isLoading ? false : aiQuery === '' || isProcessing}
            aria-disabled={aiQuery === '' || isProcessing}
            onClick={handleSubmit}
            spin={isLoading}
          />
        </IconWrapper>
      </AiQueryInputWrapper>
    </AiQueryContainer>
  );
};
